/**
 * PersonaFormDialog — Sheet Migration Wave 3b
 *
 * FormSheet wrapper for creating and editing personas. Converted from Modal
 * (Wave 3b) to provide a right-slide Sheet with dirty-state guard, discard
 * banner, and URL routing support.
 *
 * Single shared dialog for creating and editing personas from any
 * standalone entry point (Target Market tree Pencil button, Personas
 * list page, Persona detail page). Title and submit-button label
 * switch based on `mode`; internals (fields, layout, validation)
 * are 100% shared with the "Create new persona" tab in
 * `MarketTree.tsx`'s `AddPersonaModal` via `PersonaFormBody`.
 *
 * Uses:
 *   - FormSheet<PersonaSnapshot> for dirty-state guard, discard banner, header actions
 *   - useFormSnapshot<PersonaSnapshot> for per-field state management
 *   - PersonaFormBody in controlled mode (value + onChange) as the body content
 *
 * Public component API (isOpen, onClose, mode, persona, onSubmit, isSubmitting)
 * is UNCHANGED from the Modal version. urlKey and urlId are optional additions
 * for URL routing (opt-in, default off — existing callers need no changes).
 */

import React, { useCallback } from 'react';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { FormSheet } from '@/components/ui/FormSheet';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import { PersonaFormBody, type PersonaFormValues } from './PersonaFormBody';
import type { CreatePersonaPayload, RorosPersonaDTO } from '@/types/roros/overarching';

// ─── Snapshot type & diff ─────────────────────────────────────────────────────

/**
 * All 10 persona form fields as plain strings — empty string represents null.
 * Matches PersonaFormValues from PersonaFormBody so controlled mode is type-safe.
 */
type PersonaSnapshot = PersonaFormValues;

function diffPersonaSnapshots(
  base: PersonaSnapshot,
  cur: PersonaSnapshot,
): Partial<Record<keyof PersonaSnapshot, boolean>> {
  return {
    name: base.name !== cur.name,
    role: base.role !== cur.role,
    decisionRole: base.decisionRole !== cur.decisionRole,
    influenceLevel: base.influenceLevel !== cur.influenceLevel,
    demographics: base.demographics !== cur.demographics,
    communicationStyle: base.communicationStyle !== cur.communicationStyle,
    needs: base.needs !== cur.needs,
    goals: base.goals !== cur.goals,
    painPoints: base.painPoints !== cur.painPoints,
    objections: base.objections !== cur.objections,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toStr(v: string | null | undefined): string {
  return v ?? '';
}

function cleaned(s: string): string | null {
  const trimmed = s.trim();
  return trimmed === '' ? null : trimmed;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface PersonaFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  /** Required when mode === 'edit'. */
  persona?: RorosPersonaDTO | null;
  onSubmit: (draft: CreatePersonaPayload) => Promise<void> | void;
  isSubmitting?: boolean;
  /** URL routing (opt-in): when set, FormSheet syncs ?urlKey=urlId in the URL. */
  urlKey?: string;
  /** The id value written to the URL param. Required when urlKey is set. */
  urlId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PersonaFormDialog({
  isOpen,
  onClose,
  mode,
  persona,
  onSubmit,
  isSubmitting = false,
  urlKey,
  urlId,
}: PersonaFormDialogProps) {
  const isEditing = mode === 'edit';

  // ─── Form state via useFormSnapshot ──────────────────────────────────────
  // depKey = persona?.id so the snapshot re-seeds when the edited persona changes.
  // For create mode, depKey is null (undefined persona?.id → null).
  const { state, setField, baseline, current } = useFormSnapshot<PersonaSnapshot>({
    initialFactory: () =>
      persona
        ? {
            name: persona.name,
            role: toStr(persona.role),
            decisionRole: toStr(persona.decisionRole),
            influenceLevel: toStr(persona.influenceLevel),
            demographics: toStr(persona.demographics),
            communicationStyle: toStr(persona.communicationStyle),
            needs: toStr(persona.needs),
            goals: toStr(persona.goals),
            painPoints: toStr(persona.painPoints),
            objections: toStr(persona.objections),
          }
        : {
            name: '',
            role: '',
            decisionRole: '',
            influenceLevel: '',
            demographics: '',
            communicationStyle: '',
            needs: '',
            goals: '',
            painPoints: '',
            objections: '',
          },
    isOpen,
    depKey: persona?.id ?? null,
  });

  // ─── Controlled onChange adapter ─────────────────────────────────────────
  // useFormSnapshot's setField is generic; PersonaFormBody's onChange expects
  // (k: keyof PersonaFormValues, v: string). This adapter bridges them.
  const handleChange = useCallback(
    (k: keyof PersonaFormValues, v: string) => {
      setField(k, v);
    },
    [setField],
  );

  // ─── Validation & save ────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!state.name.trim()) {
      showToast.error(t('Name is required'));
      return;
    }
    const draft: CreatePersonaPayload = {
      name: state.name.trim(),
      role: cleaned(state.role),
      decisionRole: (state.decisionRole || null) as CreatePersonaPayload['decisionRole'],
      influenceLevel: (state.influenceLevel || null) as CreatePersonaPayload['influenceLevel'],
      demographics: cleaned(state.demographics),
      needs: cleaned(state.needs),
      goals: cleaned(state.goals),
      painPoints: cleaned(state.painPoints),
      objections: cleaned(state.objections),
      communicationStyle: cleaned(state.communicationStyle),
    };
    await onSubmit(draft);
  }, [state, onSubmit]);

  const title = isEditing ? t('Edit Persona') : t('Create Persona');
  const submitLabel = isEditing ? t('Save Changes') : t('Create');

  return (
    <FormSheet<PersonaSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      mode={isEditing ? 'edit' : 'create'}
      baseline={baseline}
      current={current}
      diff={diffPersonaSnapshots}
      onSubmit={handleSave}
      isSubmitting={isSubmitting}
      submitLabel={submitLabel}
      submittingLabel={t('Saving…')}
      width="md"
      urlKey={urlKey}
      urlId={urlId}
    >
      {/* PersonaFormBody in controlled mode — FormSheet provides Cancel/Save. */}
      <PersonaFormBody
        value={state}
        onChange={handleChange}
      />
    </FormSheet>
  );
}
